import { z } from 'zod';
import { DepositStatusSchema, EventIdSchema, MoneySchema, OperatorIdSchema, ReviewCaseIdSchema, TransactionIdSchema, WithdrawalIdSchema, WithdrawalStatusSchema, } from './common.js';
import { AmountAdjustmentSchema, CheckMatrixSchema, CreditSettlementSchema, VerifiedSlipSchema, } from './verification.js';
/**
 * Webhook envelope, event catalog, and delivery headers (SAD §9).
 *
 * "Delivery is at-least-once · signed · ordered per transaction · retried with backoff · DLQ ·
 * idempotently consumed. A webhook is a notification; the authoritative money decision is a
 * separate explicit API call" (`POST /transactions/{id}/credit-result`) — two-phase money ACK.
 *
 * Only `transaction.confirmed`'s payload is given verbatim in SAD §9. The other 13 event
 * payloads below follow the same `{ transaction }` / `{ withdrawal }` / `{ delivery }` shape by
 * extension — see the module-level judgment-call note in this package's delivery report for the
 * ones that are inferred rather than quoted.
 */
// ---------------------------------------------------------------------------------------------
// Event catalog (SAD §9)
// ---------------------------------------------------------------------------------------------
/**
 * The 14 Phase-1 event types (SAD §9 event catalog table) in catalog order, plus the Phase 3
 * settlement lifecycle (SAD §15, clearing M3) — delivered to BOTH websites of a settlement, and
 * never emitted while `capability_clearing` is off — and the operator-triggered re-verification
 * signal appended last (2026-08-19), which belongs to no SAD-numbered group.
 */
export const EVENT_TYPES = [
    'deposit.reserved',
    'withdrawal.reserved',
    'transaction.awaiting_evidence',
    'reservation.evidence_overdue',
    'slip.verified',
    'transaction.under_review',
    'transaction.confirmed',
    'transaction.completed',
    'transaction.rejected',
    'transaction.expired',
    'withdrawal.fulfilled',
    'withdrawal.released',
    // The fold home (0047). Delivered since the day split-merge shipped and part of
    // `WebhookEventSchema` below, but missing from this list until 2026-09-01 — and this list is
    // what `EventTypeSchema` is built from, which types both the `X-PF-Event-Type` header and
    // `DeadLetterEntrySchema.event_type`. So one dead-lettered fold made `GET /ops/v1/dlq` fail
    // response validation and answer 503 for that participant: the screen on-call opens to find
    // and replay dead letters went dark, for every participant that had ever failed to take a
    // fold. Found by the participant conformance suite (core C17).
    'withdrawal.slice_merged',
    'webhook.failed',
    'webhook.dead_lettered',
    'settlement.instructed',
    'settlement.completed',
    'settlement.failed',
    'settlement.overdue',
    'review.reverify_requested',
];
/** A Phase-1 webhook event type (SAD §9). */
export const EventTypeSchema = z.enum(EVENT_TYPES);
// ---------------------------------------------------------------------------------------------
// Webhook delivery headers (SAD §9)
// ---------------------------------------------------------------------------------------------
/** `X-PF-Event-Id` — the envelope's `id`, for delivery-layer dedupe ahead of parsing the body. */
export const EventIdHeaderSchema = EventIdSchema;
/** `X-PF-Event-Type` — the envelope's `type`, so a receiver can route before parsing the body. */
export const EventTypeHeaderSchema = EventTypeSchema;
/** `X-PF-Delivery` — this specific delivery attempt's id (`whd_<ulid>`), distinct from the event id. */
export const DeliveryHeaderSchema = z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/);
/** `X-PF-Signature` — `t=<ts>,v1=<hmac>` (SAD §9), covering `ts \n event_id \n sha256(body)`. */
export const WebhookSignatureHeaderSchema = z.string().regex(/^t=\d+,v1=[0-9a-f]{64}$/);
/** `X-PF-Key-Id` — e.g. `whk_2`; identifies which of the participant's overlapping keys signed this. */
export const KeyIdHeaderSchema = z.string().regex(/^whk_\w+$/);
/** The complete set of outbound webhook delivery headers (SAD §9). */
export const WebhookHeadersSchema = z
    .object({
    'x-pf-event-id': EventIdHeaderSchema,
    'x-pf-event-type': EventTypeHeaderSchema,
    'x-pf-delivery': DeliveryHeaderSchema,
    'x-pf-signature': WebhookSignatureHeaderSchema,
    'x-pf-key-id': KeyIdHeaderSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Shared fragments the per-event `data` blocks are built from
// ---------------------------------------------------------------------------------------------
const transactionRefSchema = z
    .object({
    id: TransactionIdSchema,
    status: DepositStatusSchema,
})
    .strict();
const withdrawalRefSchema = z
    .object({
    id: WithdrawalIdSchema,
    status: WithdrawalStatusSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Per-event envelopes
// ---------------------------------------------------------------------------------------------
const envelopeFields = {
    id: EventIdSchema,
    sequence: z.number().int().nonnegative(),
    created_at: z.string().datetime({ offset: true }),
};
/** `deposit.reserved` — Matching → Website, Audit. One active reservation per withdrawal. */
export const DepositReservedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('deposit.reserved'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({
            status: z.literal('RESERVED'),
            amount: MoneySchema,
            matched_withdrawal_id: WithdrawalIdSchema,
            reserved_until: z.string().datetime({ offset: true }),
        }),
    })
        .strict(),
})
    .strict();
/**
 * `withdrawal.reserved` — Matching → Website, Audit. Mirrors `deposit.reserved` for the withdrawal side.
 *
 * ## `lock_reference` / `origin_lock_reference` / `slice_sequence` / `amount`
 *
 * Split fill (migration 0034): present only when the reserved withdrawal is a slice. Without
 * them, a website's first sight of a slice PF minted on its own (`<parent>#<n>`) is whichever
 * terminal event settles it — `withdrawal.fulfilled` or `withdrawal.released` — and anything that
 * happens to the reservation before then (a cancel that re-pools it, 2026-08-27) is invisible:
 * the website has no lock, no amount, and nothing to attribute it to. Carrying the same triple
 * `withdrawal.fulfilled` / `withdrawal.released` carry here too closes that gap.
 *
 * All four are optional and additive — every existing payload, and every website that predates
 * split fill, stays byte-identical; absent on every whole-withdrawal reservation.
 */
export const WithdrawalReservedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('withdrawal.reserved'),
    data: z
        .object({
        withdrawal: withdrawalRefSchema.extend({
            status: z.literal('RESERVED'),
            reserved_by: TransactionIdSchema,
        }),
        lock_reference: z.string().min(1).optional(),
        origin_lock_reference: z.string().min(1).optional(),
        slice_sequence: z.number().int().positive().optional(),
        amount: MoneySchema.optional(),
    })
        .strict(),
})
    .strict();
/** `transaction.awaiting_evidence` — Orchestration → Website. Guarded by state + version. */
export const TransactionAwaitingEvidenceEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.awaiting_evidence'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({
            status: z.literal('AWAITING_EVIDENCE'),
            deadline_at: z.string().datetime({ offset: true }),
        }),
    })
        .strict(),
})
    .strict();
/** `reservation.evidence_overdue` — Orchestration timer → Review, Audit. Fires once at the deadline. */
export const ReservationEvidenceOverdueEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('reservation.evidence_overdue'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({ status: z.literal('UNDER_REVIEW') }),
        withdrawal_id: WithdrawalIdSchema,
    })
        .strict(),
})
    .strict();
/** `slip.verified` — Verification → Orchestration, Audit. Deduped globally on `trans_ref`. */
export const SlipVerifiedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('slip.verified'),
    data: z
        .object({
        transaction: transactionRefSchema,
        slip: VerifiedSlipSchema,
        checks: CheckMatrixSchema,
        /**
         * Present when the amount check failed but policy auto-reconciled the difference
         * (`amountAutoReconcileUnder`/`…Over`) — the slip was accepted for `paid_amount`, not
         * for the reserved amount. Additive and optional: absent on every exact-match slip.
         */
        adjustment: AmountAdjustmentSchema.optional(),
    })
        .strict(),
})
    .strict();
/** `transaction.under_review` — Orchestration/Verification → Review, Website. One open case per transaction. */
export const TransactionUnderReviewEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.under_review'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({ status: z.literal('UNDER_REVIEW') }),
        review_case_id: ReviewCaseIdSchema,
    })
        .strict(),
})
    .strict();
/**
 * `transaction.confirmed` — Orchestration → Website (credit), Audit. Guarded; single transition.
 *
 * The one payload SAD §9 gives verbatim:
 * ```
 * { "id":"evt_...", "type":"transaction.confirmed", "sequence":42, "created_at":"...",
 *   "data": { "transaction": { "id":"txn_...", "status":"CONFIRMED",
 *   "amount":{"value":"500.00","currency":"THB"},
 *   "verification":{"trans_ref":"01420064...","checks":{...}} } } }
 * ```
 *
 * ## `matched_withdrawal_id` and `lock_reference` (docs/plan/06-risks.md A9)
 *
 * Two fields extend that quotation, and the reason is a gap between §7.1 and §9. SAD §7.1 tells
 * the website to do **two** things on this single event — credit the depositor, *and* commit the
 * withdrawer's funds lock — but the payload as specified names neither the withdrawal nor its
 * lock. An integrator who implements exactly §7.1 and nothing more therefore credits the
 * depositor with no lock committed: money invented, which is the same failure class the §3
 * net-to-zero invariant exists to catch. Getting it right required having stored
 * `matched_withdrawal_id` earlier, from `deposit.reserved` or the reserve response — a correctness
 * requirement that lived in the integrator's memory rather than in the contract.
 *
 * Carrying both here makes the event self-contained: everything §7.1 instructs is in the message
 * that instructs it.
 *
 * **Both are optional, and that is the point.** They are additive — every existing consumer, and
 * every payload already in a delivery queue or a replayed DLQ row, stays valid. A consumer that
 * ignores them behaves exactly as before, including the existing recovery path where the commit
 * lands later on the idempotent `withdrawal.fulfilled` (SAD §9). Making them required would have
 * been a breaking change to a contract two agents are already building against, in exchange for
 * nothing a correct integration cannot get from the field being present.
 */
export const TransactionConfirmedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.confirmed'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({
            status: z.literal('CONFIRMED'),
            amount: MoneySchema,
            /**
             * The withdrawal this deposit was matched against — the one whose lock §7.1 says to
             * commit. Optional for compatibility; present on every event PaymentFlow emits.
             */
            matched_withdrawal_id: WithdrawalIdSchema.optional(),
            /**
             * The website's own funds-lock reference for that withdrawal, echoed back so the commit
             * needs no lookup. The same value the terminal `withdrawal.fulfilled` /
             * `withdrawal.released` events carry (SAD §8), which is what makes committing on either
             * event idempotent.
             */
            lock_reference: z.string().min(1).optional(),
            /**
             * Present only when the matched withdrawal is a split-fill slice: the parent lock the
             * website actually holds, plus the slice's ordinal. The credit instruction is the first
             * event a website sees for a slice PF minted on its own (`<parent>#<n>` — 0034), so
             * without these it cannot materialize the slice before the adjusted-credit path needs
             * the lock; the terminal `withdrawal.fulfilled` / `withdrawal.released` arrive too late
             * (incident 2026-08-25, txn_01M0VQZVTT7MHD33A8DNAJGC1E). The slice's own amount is not
             * repeated here: a slice is carved to its deposit's reservation, so it is always equal
             * to `amount` above.
             */
            origin_lock_reference: z.string().min(1).optional(),
            slice_sequence: z.number().int().positive().optional(),
            verification: z
                .object({
                trans_ref: z.string().min(1),
                checks: CheckMatrixSchema,
            })
                .strict(),
            /**
             * Amount auto-reconciliation instruction (additive, optional). When present, §7.1's
             * "credit the depositor and commit the lock" is refined by `direction`:
             *
             *  - `UNDER` — credit `paid_amount` (not `amount`); commit `paid_amount` of the lock
             *    and release `delta` back to the withdrawer's available balance.
             *  - `OVER` — credit `amount` plus up to `delta` more, capped by the withdrawer's
             *    available balance; debit the withdrawer that extra; commit the full lock.
             *
             * Whatever was actually moved is reported back on `credit-result.settlement` — that
             * report, not this instruction, is the authoritative record of the amounts.
             */
            withdrawer_adjustment: AmountAdjustmentSchema.optional(),
            /**
             * Clearing (SAD §15): `true` when the matched withdrawal belongs to ANOTHER website.
             * The `lock_reference` above then names a lock this website has never held and never
             * will — §7.1's "commit the withdrawer's lock" is not this website's job on this
             * match. Credit the depositor and stop: the other website commits its own lock on the
             * `withdrawal.fulfilled` it receives. Treating the foreign reference as a local lock
             * (lookups, slice materialization, lock bookkeeping) is the failure this flag exists
             * to prevent. When `withdrawer_adjustment` rides along (cross-website reconcile,
             * `amount_auto_reconcile_cross_web`), credit **`paid_amount`** — both directions; the
             * lock side of the adjustment is the other website's, instructed on ITS terminal
             * `withdrawal.fulfilled`. Omitted — not `false` — on every same-website match,
             * keeping those payloads byte-identical.
             */
            cross_website: z.literal(true).optional(),
        }),
    })
        .strict(),
})
    .strict();
/** `transaction.completed` — Orchestration → Website, Audit. Fires after `credit-result` SUCCESS. */
export const TransactionCompletedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.completed'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({
            status: z.literal('COMPLETED'),
            amount: MoneySchema,
            /**
             * True when this completion came from an `EXTERNAL` review decision — settled
             * manually outside the credit rails, so no `transaction.confirmed` credit
             * instruction preceded this event and no `credit-result` will follow. The website's
             * recon backstop must skip crediting these; the money already moved by hand.
             * Additive and optional; absent means a normal credit-rail completion.
             */
            settled_externally: z.boolean().optional(),
        }),
        /**
         * What the website's ledger actually moved, as it reported on `credit-result` — present
         * only when the credit carried a `withdrawer_adjustment`. Additive and optional.
         */
        settlement: CreditSettlementSchema.optional(),
    })
        .strict(),
})
    .strict();
/** `transaction.rejected` — Orchestration → Website, Audit. Terminal; slot released. */
export const TransactionRejectedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.rejected'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({ status: z.literal('REJECTED') }),
        reason: z.enum(['DUPLICATE_TRANS_REF', 'NOT_A_SLIP', 'OPERATOR_REJECTED']),
    })
        .strict(),
})
    .strict();
/** `transaction.expired` — Orchestration → Website, Audit. Terminal; slot released. */
export const TransactionExpiredEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('transaction.expired'),
    data: z
        .object({
        transaction: transactionRefSchema.extend({ status: z.literal('EXPIRED') }),
    })
        .strict(),
})
    .strict();
/**
 * `withdrawal.fulfilled` — Orchestration → Website (confirmation; the commit already applied on
 * `transaction.confirmed`, so this is a no-op if so). Idempotent on `(lock_reference, FULFILLED)`.
 *
 * Terminal withdrawal event — **must carry `lock_reference`** (SAD §8: "Terminal webhooks
 * withdrawal.fulfilled / withdrawal.released carry the lock_reference so the website commits or
 * releases its funds lock").
 *
 * ## `matched_transaction_id`
 *
 * The deposit transaction that paid this withdrawal — the same additive pattern as
 * `transaction.confirmed`'s `matched_withdrawal_id` (see the rationale on
 * {@link TransactionConfirmedEventSchema}), pointing the other way. Without it the terminal
 * withdrawal event names no transaction, so a website tracing a *fulfilled* withdrawal back to
 * its slip had to have remembered `reserved_by` from `withdrawal.reserved` — a pairing that can
 * be replaced on re-reservation and is authoritative only here, at the terminal state. Optional
 * for compatibility; present on every event PaymentFlow emits.
 */
export const WithdrawalFulfilledEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('withdrawal.fulfilled'),
    data: z
        .object({
        withdrawal: withdrawalRefSchema.extend({
            status: z.literal('FULFILLED'),
            matched_transaction_id: TransactionIdSchema.optional(),
        }),
        lock_reference: z.string().min(1),
        /**
         * How the lock was settled when the paying credit carried a `withdrawer_adjustment`:
         * committed short with `released_amount` returned (UNDER), or committed in full with
         * `extra_debited_amount` taken on top (OVER). Absent on the exact-match path, where the
         * whole lock commits. Additive and optional.
         *
         * On a SAME-website match this echoes what the website itself reported — an audit read.
         * On a CROSS-website match it is this website's **instruction**: the crediting website
         * never touched the lock (it reported only `credited_amount`; PaymentFlow completed the
         * released/extra arithmetic against the reserved amount), and this event is the first
         * and only place the lock's owner is told how to settle it — UNDER: commit the paid
         * share, release `released_amount` to the withdrawer; OVER: commit in full and debit up
         * to `extra_debited_amount` more, capped by the withdrawer's balance, any shortfall
         * being this website's own receivable.
         */
        settlement: CreditSettlementSchema.optional(),
        /**
         * Split fill (migration 0034) — present only when this withdrawal is a *slice*.
         *
         * `origin_lock_reference` is the reference the website's single funds lock is actually
         * filed under; `lock_reference` above is this slice's derived `<origin>#<n>`. A website
         * that treated the derived one as the parent's would settle a whole lock for a partial
         * payment, which is the one failure mode split fill can produce, so both are sent rather
         * than leaving the decomposition to string handling at the far end.
         *
         * `amount` is the slice's own amount — what this instruction is worth against that lock.
         */
        origin_lock_reference: z.string().min(1).optional(),
        slice_sequence: z.number().int().positive().optional(),
        amount: MoneySchema.optional(),
        /**
         * Split fill (migration 0034) — present only when this withdrawal is a split *parent*.
         *
         * PaymentFlow shrinks the parent as it cuts slices, so what remains under
         * `lock_reference` at terminal time is smaller than the amount the website locked. A
         * website that commits its own stored figure commits the slices' share a second time —
         * observed 2026-08-27. This states the authoritative residual on the event that closes
         * the lock, so committing at a stale amount takes ignoring a field rather than merely
         * missing a slice event.
         *
         * Distinct from `amount` above on purpose: that one means "this slice's share of someone
         * else's lock" and travels with `origin_lock_reference`. Never both on one payload.
         *
         * ### With `settlement`
         *
         * When both are present, `residual_amount` is what the lock is **worth** and `settlement`
         * says what was done **against it**. An under-paid reconcile therefore means
         * `credited_amount + released_amount === residual_amount` — never "commit
         * `residual_amount` in full, and release on top of that". The residual is the ceiling the
         * settlement is spent out of, not an instruction of its own.
         *
         * ### Why not `amount`
         *
         * The recon feed (`GET /v1/withdrawals`, `WithdrawalReconItemSchema`) exposes this very
         * figure under the name `amount`, because there it is simply the row's own amount. Here
         * it cannot be called that: on these two events `amount` already means "a slice's share
         * of a lock filed elsewhere", and one key that means two things on the same payload is
         * how a whole lock gets settled for a partial payment.
         *
         * The ops read (`WithdrawalLineageTotalsSchema.residual_amount`) is the same figure again,
         * but do not diff the two strings: that route renders at storage scale (`"4000.0000"`)
         * while this wire renders at the currency's own (`"4000.00"`). Compare as decimals.
         */
        residual_amount: MoneySchema.optional(),
    })
        .strict(),
})
    .strict();
/**
 * `withdrawal.released` — Orchestration → Website (release lock). Idempotent on
 * `(lock_reference, terminal)`.
 *
 * Terminal withdrawal event — **must carry `lock_reference`** (SAD §8, see `WithdrawalFulfilledEventSchema`).
 */
export const WithdrawalReleasedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('withdrawal.released'),
    data: z
        .object({
        withdrawal: withdrawalRefSchema.extend({
            status: z.enum(['AVAILABLE', 'CANCELLED', 'EXPIRED']),
        }),
        lock_reference: z.string().min(1),
        /**
         * Split fill (migration 0034) — present only when this withdrawal is a *slice*.
         *
         * `origin_lock_reference` is the reference the website's single funds lock is actually
         * filed under; `lock_reference` above is this slice's derived `<origin>#<n>`. A website
         * that treated the derived one as the parent's would settle a whole lock for a partial
         * payment, which is the one failure mode split fill can produce, so both are sent rather
         * than leaving the decomposition to string handling at the far end.
         *
         * `amount` is the slice's own amount — what this instruction is worth against that lock.
         */
        origin_lock_reference: z.string().min(1).optional(),
        slice_sequence: z.number().int().positive().optional(),
        amount: MoneySchema.optional(),
        /**
         * Split fill (migration 0034) — present only when this withdrawal is a split *parent*.
         *
         * The residual left under `lock_reference` once every slice was cut: what this release is
         * actually for, and the whole of it — a release carries no `settlement`. Releasing the
         * website's own stored figure would hand the withdrawer back money already paid out
         * through a slice. Same field, same meaning, as on
         * {@link WithdrawalFulfilledEventSchema} — the two terminal events are one contract.
         *
         * As there, the recon feed (`GET /v1/withdrawals`, `WithdrawalReconItemSchema`) carries
         * this same figure under the name `amount`; it cannot be called that here, because on
         * these events `amount` means "a slice's share of a lock filed elsewhere".
         */
        residual_amount: MoneySchema.optional(),
        /**
         * What the operator who cancelled this withdrawal wants done with the released money.
         *
         * Present **only** when an operator chose it in the console; absent on every other
         * release — the withdrawer's own cancel, the max-age sweep, and an ordinary operator
         * cancel alike — and absent is the instruction this event has always carried: release
         * the lock, the money is the customer's to sit on.
         *
         * `MOVE_TO_NORMAL` says the customer could not drive the next step themselves, so the
         * website should re-file the released amount as an ordinary withdrawal in its own payout
         * queue rather than leave it in their wallet. It is an instruction about the *website's*
         * ledger, not about PF's: the row here is CANCELLED either way, and PF has no further
         * part in the money. A website that does not implement it releases, which is the correct
         * fallback and the reason it is a single optional field rather than a new event.
         *
         * Deliberately **not** on the reconciliation feed (`WithdrawalReconItemSchema`). If this
         * event is lost and the feed heals the release instead, the money returns to the wallet
         * and the operator repeats the action — a benign degradation, where carrying the
         * instruction on a feed that is re-read for healing would risk filing the same payout
         * twice.
         */
        disposition: z.literal('MOVE_TO_NORMAL').optional(),
    })
        .strict(),
})
    .strict();
/**
 * `withdrawal.slice_merged` — Orchestration → Website (fold a slice's lock back into its origin).
 * Idempotent on `lock_reference`.
 *
 * The undo of the carve `withdrawal.reserved` announced. A slice whose depositor lapsed goes back
 * on the book as a fragment only its own exact size can match; when the row it was cut from is
 * still AVAILABLE and unclaimed, PaymentFlow folds the fragment home instead — the origin holds
 * one whole amount again and can be re-cut at whatever size the next depositor brings. Slicing a
 * withdrawal and abandoning it therefore leaves no permanent mark, which is the point: without
 * this, three lapsed reservations shatter a request into pieces nobody wants and spend its whole
 * `split_max_slices` budget doing it.
 *
 * **This event moves no member money and must not be treated as a release.** Both locks are held
 * throughout: `amount` leaves the slice's lock and lands in the origin's, and the withdrawer's
 * available balance is untouched at every instant. A website that released here would hand back
 * money it is still on the hook to pay out. The slice's row goes terminal at `MERGED`, a state
 * that exists for exactly this and means "folded home", never "settled".
 *
 * Sent only to a participant carrying `capability_slice_merge` — a website that has not
 * implemented the fold is never sent one, and PaymentFlow leaves its fragments on the book as
 * before, exactly as `capability_split_fill` gates the carve itself.
 *
 * Mirrored on the recon feed (`WithdrawalReconItemSchema`) as a `MERGED` row carrying the same
 * `amount` and `origin_lock_reference`, so a lost delivery heals on the next poll. Unlike
 * `disposition` on `withdrawal.released` — which is deliberately kept off that feed because
 * re-reading it could file a second payout — re-reading this one is safe: folding an already
 * folded slice is a no-op against its own terminal state.
 */
export const WithdrawalSliceMergedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('withdrawal.slice_merged'),
    data: z
        .object({
        withdrawal: withdrawalRefSchema.extend({
            status: z.literal('MERGED'),
        }),
        /** This slice's own derived reference, `<origin>#<n>` — the lock being closed. */
        lock_reference: z.string().min(1),
        /** The reference the website's real funds lock is filed under — the lock being grown. */
        origin_lock_reference: z.string().min(1),
        /** This slice's position within its origin, 1-based; the `#n` above, stated rather than parsed. */
        slice_sequence: z.number().int().positive(),
        /**
         * What moves from the slice's lock to the origin's — the slice's own amount, and the
         * whole of it. A slice is folded home entire or not at all; there is no partial fold.
         *
         * Required here, unlike on the terminal events where it is optional: a fold with no
         * figure is not a weaker instruction, it is an unexecutable one.
         */
        amount: MoneySchema,
    })
        .strict(),
})
    .strict();
/** `webhook.failed` — Webhook Dispatcher → Ops/Alerting. Keyed on `(event_id, participant, attempt)`. */
export const WebhookFailedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('webhook.failed'),
    data: z
        .object({
        delivery_id: z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/),
        failed_event_id: EventIdSchema,
        participant: z.string().min(1),
        attempt: z.number().int().positive(),
        status_code: z.number().int().optional(),
        error: z.string().optional(),
    })
        .strict(),
})
    .strict();
/** `webhook.dead_lettered` — Webhook Dispatcher → Ops/Alerting. Attempt 8 exhausted → DLQ + alert. */
export const WebhookDeadLetteredEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('webhook.dead_lettered'),
    data: z
        .object({
        delivery_id: z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/),
        failed_event_id: EventIdSchema,
        participant: z.string().min(1),
        attempts: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
/**
 * The settlement lifecycle's shared `data` (SAD §15, clearing M3): who pays whom, how much, and
 * the window of obligations the figure covers. Delivered to BOTH websites of the settlement —
 * the debtor because it must move real money, the creditor because it should expect it.
 */
const settlementDataSchema = z
    .object({
    settlement_id: z.string().regex(/^stl_[0-9A-HJKMNP-TV-Z]{26}$/),
    debtor: z.string().min(1),
    creditor: z.string().min(1),
    /** Decimal string, like every amount on the wire (SAD §8). */
    net_amount: z.string().min(1),
    currency: z.string().length(3),
    window_start: z.string().datetime({ offset: true }),
    window_end: z.string().datetime({ offset: true }),
})
    .strict();
/** `settlement.instructed` — Clearing → both websites: the debtor must now transfer the net. */
export const SettlementInstructedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('settlement.instructed'),
    data: settlementDataSchema,
})
    .strict();
/** `settlement.completed` — Clearing → both websites: the transfer is certified done. */
export const SettlementCompletedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('settlement.completed'),
    data: settlementDataSchema,
})
    .strict();
/** `settlement.failed` — Clearing → both websites: it did not happen; the exposure is live again. */
export const SettlementFailedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('settlement.failed'),
    data: settlementDataSchema,
})
    .strict();
/**
 * `settlement.overdue` — Clearing → both websites: instructed, and still unpaid past its window.
 *
 * Sent once per settlement, to the debtor as a demand and to the creditor as a warning about
 * money it is owed. While it stands, the debtor is excluded from other websites' liquidity.
 */
export const SettlementOverdueEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('settlement.overdue'),
    data: settlementDataSchema,
})
    .strict();
/**
 * `review.reverify_requested` — Audit, and the participant's transaction event history: an
 * operator sent a case that was opened only because the slip provider could not be reached back
 * through verification.
 *
 * **This is not a decision about the money.** A `PROVIDER_UNAVAILABLE` case exists because the
 * question was never asked, not because the answer was doubtful, so re-asking it decides nothing:
 * no credit is instructed, no funds lock is released, and the transaction returns to VERIFYING to
 * be settled by the provider's attestation exactly as a first-time slip would be. The eventual
 * outcome arrives on the ordinary `slip.verified` / `transaction.confirmed` / `transaction.rejected`
 * events. Receivers that reconcile on money movement should ignore this one; it exists so the
 * hash-chained log — and the participant reading it — can explain why a case that was under review
 * is suddenly verifying again, and who asked for that.
 *
 * Catalogued here — rather than left out as an internal extension — so it is readable on
 * `GET /v1/transactions/{id}` and so any envelope carrying it validates. **Delivery is a separate
 * decision and today the answer is no**: this event is emitted by a deposit *transition*, and the
 * transition path enqueues webhooks from `@pf/application`'s own `WEBHOOK_EVENT_CATALOG`, which
 * does not list it. Being in this array does not by itself put an event on a participant's
 * webhook endpoint.
 *
 * `data` carries the requesting operator only, and carries it as `operatorId` rather than the
 * `operator_id` the rest of this file uses: the domain payload is passed through verbatim by
 * `defaultWebhookPayloadBuilder` (there is no `dataFor` arm to rename it), and this schema
 * describes what actually goes on the wire, not what the naming convention would prefer.
 */
export const ReviewReverifyRequestedEventSchema = z
    .object({
    ...envelopeFields,
    type: z.literal('review.reverify_requested'),
    data: z.object({ operatorId: OperatorIdSchema }).strict(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// The union
// ---------------------------------------------------------------------------------------------
/**
 * The complete webhook envelope: a discriminated union over all 14 Phase-1 event types (SAD §9),
 * the 4 Phase-3 settlement events, `withdrawal.slice_merged` and `review.reverify_requested`,
 * discriminated on `type`. Every variant shares `{id, type, sequence, created_at, data}`.
 */
export const WebhookEventSchema = z.discriminatedUnion('type', [
    DepositReservedEventSchema,
    WithdrawalReservedEventSchema,
    TransactionAwaitingEvidenceEventSchema,
    ReservationEvidenceOverdueEventSchema,
    SlipVerifiedEventSchema,
    TransactionUnderReviewEventSchema,
    TransactionConfirmedEventSchema,
    TransactionCompletedEventSchema,
    TransactionRejectedEventSchema,
    TransactionExpiredEventSchema,
    WithdrawalFulfilledEventSchema,
    WithdrawalReleasedEventSchema,
    WithdrawalSliceMergedEventSchema,
    WebhookFailedEventSchema,
    WebhookDeadLetteredEventSchema,
    SettlementInstructedEventSchema,
    SettlementCompletedEventSchema,
    SettlementFailedEventSchema,
    SettlementOverdueEventSchema,
    ReviewReverifyRequestedEventSchema,
]);
//# sourceMappingURL=webhooks.js.map